import React, { useState } from 'react';
import { Dialog, Tooltip } from '@mui/material';
import toast from 'react-hot-toast';
import { IconFlag, IconSend } from 'components/studentActionBar/gmModal';
import { flagsApi } from 'services/camMirrorService';
import { FLAG_ISSUE_TYPE_OPTIONS } from 'services/flagIssueService';
import type { FlagIssueType } from 'services/flagIssueService';
import 'components/studentActionBar/studentActionBar.css';

const MAX_LEN = 500;

const FlagIssueButton: React.FC<{ studentId?: string | number; viewerRole?: string }> = ({
  studentId,
  viewerRole,
}) => {
  const [open, setOpen] = useState(false);
  const [issueType, setIssueType] = useState<FlagIssueType | ''>('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!message.trim() || !issueType) return;
    setSubmitting(true);
    try {
      const numericStudentId = Number(studentId);
      await flagsApi.submit({
        issueType,
        message: message.trim(),
        context: {
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
          studentId: Number.isFinite(numericStudentId) && numericStudentId > 0 ? numericStudentId : undefined,
          userRole: viewerRole,
        },
      });
      toast.success("Thanks for flagging this. We'll look into it and follow up if we need more info.");
      setOpen(false);
      setIssueType('');
      setMessage('');
    } catch (err) {
      toast.error('Unable to submit your report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Flag an Issue" placement="bottom" enterDelay={200}>
        <button type="button" className="gm-flag-trigger" aria-label="Flag an Issue" onClick={() => setOpen(true)}>
          <IconFlag />
        </button>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} PaperProps={{ className: 'gm-modal gm-modal-report' }}>
        <div className="gm-report-header">
          <div className="gm-report-title">
            <IconFlag />
            Report an issue
          </div>
          <div className="gm-report-sub">Something wrong on this page? Tell the GradMap team so we can fix it.</div>
        </div>
        <div className="gm-modal-body">
          <div className="gm-report-field">
            <label htmlFor="gm-flag-type">What went wrong?</label>
            <select
              id="gm-flag-type"
              value={issueType}
              onChange={(event) => setIssueType(event.target.value as FlagIssueType)}
            >
              <option value="" disabled>
                Select an issue type
              </option>
              {FLAG_ISSUE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <label htmlFor="gm-flag-message">Tell us more</label>
            <textarea
              id="gm-flag-message"
              placeholder="Describe what happened. Include the student name and page you were on if that helps."
              value={message}
              onChange={(event) => setMessage(event.target.value.slice(0, MAX_LEN))}
            />
            <div className="gm-char-count">
              {message.length}/{MAX_LEN}
            </div>
          </div>
        </div>
        <div className="gm-report-actions">
          <button type="button" className="gm-btn-secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="gm-btn-primary"
            onClick={handleSubmit}
            disabled={!message.trim() || !issueType || submitting}
          >
            <IconSend />
            {submitting ? 'Sending…' : 'Send'}
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default FlagIssueButton;
